/**
 * Layout utilities for workflow graphs using ELK (Eclipse Layout Kernel).
 *
 * We use elkjs' `layered` algorithm (a Sugiyama-style layered DAG layout) which
 * — unlike the previous hand-rolled recursive placer — correctly handles:
 *   - diamonds that FAN OUT then RE-CONVERGE (e.g. a decision node branching to
 *     two paths that both rejoin at `review`),
 *   - arbitrary branch counts (not just 2),
 *   - centering of each node over/under its neighbours,
 *   - orthogonal edge routing with minimal crossings.
 */

import ELK from 'elkjs/lib/elk.bundled.js';

// One reusable instance (the bundled build runs layout inline, no worker file).
const elk = new ELK();

const NODE_HEIGHT = 90; // fallback-only spacing when ELK is unavailable

// Per-node ELK footprint. Crucial for STRAIGHT horizontal edges: the left/right
// handles are pinned to each node's icon box (88px for action/decision nodes),
// while a terminal is a short pill — so ELK must know the real heights to align
// their port centers on one line. Mismatched heights (e.g. a uniform 90) put a
// pill's center above a square's center → smoothstep adds an ugly step.
function nodeSize(node) {
  if (node.type === 'terminal') return { width: 120, height: 36 };
  return { width: 96, height: 88 };
}

const ELK_OPTIONS = {
  'elk.algorithm': 'layered',
  'elk.direction': 'RIGHT',
  // Left→right flow: between-layers gap is now horizontal, nodeNode is the
  // vertical gap between sibling branches. A bit more between-layers room so the
  // node labels (which sit BELOW each node) don't crowd the next column.
  'elk.layered.spacing.nodeNodeBetweenLayers': '90',
  // Vertical gap between sibling branches. ELK only knows each node's icon-box
  // footprint (88px), but the rendered node also has a name AND now a one-line
  // description caption BELOW the icon. Reserve enough vertical room here so the
  // caption sits inside the row and never bleeds into / overlaps the row below.
  // (Was 55 before per-node captions; +~18 for the single caption line.)
  'elk.spacing.nodeNode': '74',
  // Clearance so a routed "skip" edge's lane sits clearly off the node row.
  'elk.spacing.edgeNode': '34',
  // Keep PARALLEL edges from collapsing onto one line. A long convergence edge
  // (e.g. resolve_decision → notify "no match", which spans the whole fix lane)
  // otherwise routes right alongside the backbone and the two dashed lines blur
  // into one. edgeEdge spaces edges sharing a region; edgeEdgeBetweenLayers is
  // the key one — it pushes such a long edge into its OWN horizontal lane,
  // clear of the backbone. Only affects graphs that actually have parallel
  // edges, so a single-backbone graph is unchanged.
  'elk.spacing.edgeEdge': '20',
  'elk.layered.spacing.edgeEdgeBetweenLayers': '22',
  // Tight, balanced placement that centers parents over their children.
  'elk.layered.nodePlacement.strategy': 'BRANDES_KOEPF',
  'elk.layered.crossingMinimization.strategy': 'LAYER_SWEEP',
  // Respect the order nodes/edges were declared so branches stay stable.
  'elk.layered.considerModelOrder.strategy': 'NODES_AND_EDGES',
  'elk.edgeRouting': 'ORTHOGONAL',
};

/**
 * Apply ELK auto-layout to React Flow elements.
 * @param {Array} nodes - React Flow nodes
 * @param {Array} edges - React Flow edges
 * @returns {Promise<{nodes, edges}>} - Layouted elements (edges unchanged)
 */
export async function getLayoutedElements(nodes, edges) {
  if (!Array.isArray(nodes) || nodes.length === 0) {
    return { nodes: nodes || [], edges: edges || [] };
  }

  // Only feed ELK edges whose endpoints are real nodes in this graph (defensive
  // against stray END/START or dangling edges that would make ELK throw).
  const nodeIds = new Set(nodes.map((n) => n.id));
  const elkEdges = (edges || [])
    .filter((e) => nodeIds.has(e.source) && nodeIds.has(e.target))
    .map((e, i) => ({
      id: e.id || `e${i}`,
      sources: [e.source],
      targets: [e.target],
    }));

  const elkGraph = {
    id: 'root',
    layoutOptions: ELK_OPTIONS,
    children: nodes.map((n) => ({ id: n.id, ...nodeSize(n) })),
    edges: elkEdges,
  };

  try {
    const laidOut = await elk.layout(elkGraph);
    const posById = new Map(
      (laidOut.children || []).map((c) => [c.id, { x: c.x ?? 0, y: c.y ?? 0 }]),
    );
    const layoutedNodes = nodes.map((node) => ({
      ...node,
      position: posById.get(node.id) || { x: 0, y: 0 },
    }));

    // ── Straighten the backbone ───────────────────────────────────────
    // ELK balances a decision node between its children, so the main flow
    // stair-steps ~14px per branch (each forward edge then needs an ugly
    // 2-corner jog). Fix: a node that is the SOLE non-terminal in its layer
    // (ELK x-bucket) is on the backbone — align all such nodes to one center Y.
    // Terminal pills (START/END) and genuine parallel branches (2+ non-terminals
    // sharing a layer) keep ELK's vertical offset, so nothing overlaps.
    const isTerminal = (n) => n.type === 'terminal';
    const hOf = (n) => nodeSize(n).height;
    const byLayer = new Map();
    for (const n of layoutedNodes) {
      if (isTerminal(n)) continue;
      // Bucket by x with tolerance. ELK can place two PARALLEL-branch nodes a
      // few px apart in x (e.g. a decision diamond at x=796 vs an action at
      // x=792 in the sibling branch). Exact rounding put each in its OWN bucket
      // → both looked like solo "backbone" nodes → both got pulled to the median
      // center Y → they STACKED on top of each other. Snapping to a coarse grid
      // (layers are ~180px apart, so /50 never merges genuine layers) keeps
      // same-layer siblings in one bucket, so a real 2+-node layer is never
      // mistaken for the spine.
      const x = Math.round(n.position.x / 50) * 50;
      if (!byLayer.has(x)) byLayer.set(x, []);
      byLayer.get(x).push(n);
    }
    const backbone = [...byLayer.values()].filter((arr) => arr.length === 1).map((a) => a[0]);
    const backboneIds = new Set(backbone.map((n) => n.id));
    if (backbone.length >= 2) {
      const centers = backbone.map((n) => n.position.y + hOf(n) / 2).sort((a, b) => a - b);
      const targetCY = centers[Math.floor(centers.length / 2)]; // median
      for (const n of backbone) {
        n.position = { ...n.position, y: targetCY - hOf(n) / 2 };
      }
      // Also pull the entry/final terminals onto the line. START is always on
      // the spine. An END is on the spine only when (a) its source is ITSELF on
      // the backbone, and (b) that END is the source's sole successor (a natural
      // finish, e.g. generate_script → End). If the source is an OFFSET branch
      // (a parallel row that isn't the spine — e.g. `reply` sharing a layer with
      // `review`), its End must stay on that branch's row, NOT jump to center —
      // otherwise it lands on top of whatever backbone node shares the End's
      // layer (the bug: reply→End__1 overlapping the `notify_decision` Condition).
      // An early-exit End whose source also forks (a decision) likewise stays off.
      const outDeg = new Map();
      const sourceOf = new Map();
      for (const e of edges) {
        outDeg.set(e.source, (outDeg.get(e.source) || 0) + 1);
        sourceOf.set(e.target, e.source);
      }
      for (const n of layoutedNodes) {
        if (!isTerminal(n)) continue;
        const isStart = n.data?.terminal === 'start' || n.id === 'START';
        const src = sourceOf.get(n.id);
        const onSpine = isStart
          || (src && backboneIds.has(src) && outDeg.get(src) === 1);
        if (onSpine) {
          n.position = { ...n.position, y: targetCY - hOf(n) / 2 };
        }
      }
    }

    // ── Edge source handles (from FINAL positions) ────────────────────
    // Built-in smoothstep, always anchored to real handles. Pick the SOURCE
    // handle by the target's position relative to the source: target ABOVE →
    // leave from top, BELOW → bottom, same row → right. This keeps a decision's
    // forward leg straight while its other leg drops/rises cleanly. Targets are
    // entered from the left in this left→right flow (targetHandle = 'target-left').
    const cy = new Map(layoutedNodes.map((n) => [n.id, n.position.y + hOf(n) / 2]));
    const cx = new Map(layoutedNodes.map((n) => [n.id, n.position.x + nodeSize(n).width / 2]));
    // Graph vertical center — used to bow a SKIP edge OUTWARD (away from the
    // dense middle) instead of always downward. A node in the UPPER half routes
    // its skip out the TOP (up, into empty canvas above); a node in the LOWER
    // half routes out the BOTTOM. So a backbone-row skip never detours DOWN
    // through the branch lanes hanging below it (the "inner" collision).
    const _allCy = layoutedNodes.map((n) => cy.get(n.id)).filter((v) => v != null);
    const graphMidY = _allCy.length
      ? (Math.min(..._allCy) + Math.max(..._allCy)) / 2
      : 0;
    // Center-Y gap above which an edge leaves top/bottom instead of straight.
    // Backbone-straightening can leave a convergence node ~30px off its
    // predecessor (e.g. notify pulled to the spine while its source open_pr
    // stays on the fix row) — that near-miss must still read as "same row" and
    // go STRAIGHT, else open_pr→notify drops to the bottom lane and collides
    // with the pr_decision→notify "no fix" skip edge. Genuine parallel branches
    // sit ~100px+ apart, so 44 cleanly separates artifact (~30) from real fork.
    const THRESH = 44;
    // Does a straight same-row edge u→w pass OVER an intermediate node? (e.g.
    // pr_decision → notify "no fix" skipping open_pr, which sits between them on
    // the same row.) If so the straight line — and its label — render on top of
    // that node, so the edge must detour around it (direction chosen below:
    // outward, away from the graph's center).
    const skipsANode = (e, sy) => {
      const lo = Math.min(cx.get(e.source), cx.get(e.target));
      const hi = Math.max(cx.get(e.source), cx.get(e.target));
      return layoutedNodes.some((v) =>
        v.id !== e.source && v.id !== e.target && !isTerminal(v)
        && cx.get(v.id) > lo && cx.get(v.id) < hi
        && Math.abs(cy.get(v.id) - sy) <= THRESH);
    };
    const layoutedEdges = edges.map((e) => {
      const sy = cy.get(e.source);
      const ty = cy.get(e.target);
      if (sy == null || ty == null) return e;
      if (ty < sy - THRESH) return { ...e, sourceHandle: 'source-top' };
      if (ty > sy + THRESH) return { ...e, sourceHandle: 'bottom' };
      // same row → straight out the right, UNLESS it would cut through a node.
      // Then detour OUTWARD: upper-half source bows UP (into empty canvas above),
      // lower-half source bows DOWN — never inward across the other lanes.
      if (skipsANode(e, sy)) {
        return { ...e, sourceHandle: sy <= graphMidY ? 'source-top' : 'bottom' };
      }
      return { ...e, sourceHandle: 'right' };
    });

    // ── Target handles for CONVERGING edges ───────────────────────────
    // Two+ edges entering the same node (e.g. fix_intake "has issueId" and
    // resolve_decision "found" both → fetch_issue) otherwise pile into one
    // entry point and overlap right before it. Spread them across distinct
    // target handles: ordered by source x, the farthest-left source enters from
    // the TOP (it travels across the row anyway), the rest from the left (and
    // right for a 3rd). A node with a SINGLE incoming edge is left untouched, so
    // every non-converging graph renders exactly as before (zero regression).
    const inByTarget = new Map();
    for (const e of layoutedEdges) {
      if (!inByTarget.has(e.target)) inByTarget.set(e.target, []);
      inByTarget.get(e.target).push(e);
    }
    const targetHandleOf = new Map();
    for (const [, group] of inByTarget) {
      if (group.length <= 1) continue; // single incoming → unchanged
      const sorted = [...group].sort((a, b) => (cx.get(a.source) ?? 0) - (cx.get(b.source) ?? 0));
      sorted.forEach((e, i) => {
        let h;
        if (group.length === 2) h = i === 0 ? 'top' : 'target-left';
        else if (i === 0) h = 'top';
        else if (i === sorted.length - 1) h = 'target-right';
        else h = 'target-left';
        targetHandleOf.set(e, h);
      });
    }
    const finalEdges = layoutedEdges.map((e) =>
      (targetHandleOf.has(e) ? { ...e, targetHandle: targetHandleOf.get(e) } : e));

    return { nodes: layoutedNodes, edges: finalEdges };
  } catch {
    // ELK failed (shouldn't normally) — fall back to a simple readable column so
    // the canvas is never empty / stacked at the origin.
    const layoutedNodes = nodes.map((node, i) => ({
      ...node,
      position: { x: 0, y: i * (NODE_HEIGHT + 70) },
    }));
    return { nodes: layoutedNodes, edges };
  }
}
